
import { useState } from 'react';

interface FileNode {
  id: string;
  name: string;
  type: 'file' | 'folder';
  path: string;
  children?: FileNode[];
  size?: string;
  modified?: string;
}

const CodeEditor = () => {
  const [selectedFile, setSelectedFile] = useState<FileNode | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['src', 'pages', 'components']));
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'editor' | 'preview' | 'console'>('editor');
  const [fileContent, setFileContent] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const fileTree: FileNode[] = [
    {
      id: 'src',
      name: 'src',
      type: 'folder',
      path: 'src',
      children: [
        {
          id: 'pages',
          name: 'pages',
          type: 'folder',
          path: 'src/pages',
          children: [
            { id: 'home', name: 'home', type: 'folder', path: 'src/pages/home', children: [
              { id: 'home-page', name: 'page.tsx', type: 'file', path: 'src/pages/home/page.tsx', size: '2.4 KB', modified: '2024-01-15' }
            ]},
            { id: 'dashboard', name: 'dashboard', type: 'folder', path: 'src/pages/dashboard', children: [
              { id: 'dashboard-page', name: 'page.tsx', type: 'file', path: 'src/pages/dashboard/page.tsx', size: '5.2 KB', modified: '2024-01-14' },
              { id: 'dashboard-components', name: 'components', type: 'folder', path: 'src/pages/dashboard/components', children: [
                { id: 'dashboard-header', name: 'DashboardHeader.tsx', type: 'file', path: 'src/pages/dashboard/components/DashboardHeader.tsx', size: '3.1 KB', modified: '2024-01-13' },
                { id: 'sidebar', name: 'Sidebar.tsx', type: 'file', path: 'src/pages/dashboard/components/Sidebar.tsx', size: '4.8 KB', modified: '2024-01-12' }
              ]}
            ]},
            { id: 'admin', name: 'admin', type: 'folder', path: 'src/pages/admin', children: [
              { id: 'admin-page', name: 'page.tsx', type: 'file', path: 'src/pages/admin/page.tsx', size: '8.7 KB', modified: '2024-01-11' },
              { id: 'admin-components', name: 'components', type: 'folder', path: 'src/pages/admin/components', children: [
                { id: 'admin-header', name: 'AdminHeader.tsx', type: 'file', path: 'src/pages/admin/components/AdminHeader.tsx', size: '2.9 KB', modified: '2024-01-10' },
                { id: 'admin-sidebar', name: 'AdminSidebar.tsx', type: 'file', path: 'src/pages/admin/components/AdminSidebar.tsx', size: '3.5 KB', modified: '2024-01-09' }
              ]}
            ]}
          ]
        },
        {
          id: 'components',
          name: 'components',
          type: 'folder',
          path: 'src/components',
          children: [
            { id: 'base', name: 'base', type: 'folder', path: 'src/components/base', children: [] },
            { id: 'feature', name: 'feature', type: 'folder', path: 'src/components/feature', children: [] }
          ]
        },
        { id: 'app', name: 'App.tsx', type: 'file', path: 'src/App.tsx', size: '1.2 KB', modified: '2024-01-08' },
        { id: 'main', name: 'main.tsx', type: 'file', path: 'src/main.tsx', size: '0.8 KB', modified: '2024-01-07' }
      ]
    },
    { id: 'package', name: 'package.json', type: 'file', path: 'package.json', size: '2.1 KB', modified: '2024-01-06' },
    { id: 'vite-config', name: 'vite.config.ts', type: 'file', path: 'vite.config.ts', size: '0.9 KB', modified: '2024-01-05' }
  ];

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const selectFile = (file: FileNode) => {
    if (file.type === 'file') {
      if (hasUnsavedChanges) {
        if (!confirm('You have unsaved changes. Do you want to discard them?')) {
          return;
        }
      }
      setSelectedFile(file);
      setFileContent(getFileContent(file));
      setHasUnsavedChanges(false);
    }
  };

  const handleContentChange = (newContent: string) => {
    setFileContent(newContent);
    setHasUnsavedChanges(true);
  };

  const handleSave = () => {
    if (selectedFile) {
      console.log('Saving file:', selectedFile.path);
      console.log('Content:', fileContent);
      alert(`File saved successfully: ${selectedFile.name}\n\nNote: In a production environment, this would update the actual file.`);
      setHasUnsavedChanges(false);
    }
  };

  const handleDownload = () => {
    if (selectedFile) {
      const blob = new Blob([fileContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedFile.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const renderFileTree = (nodes: FileNode[], depth = 0) => {
    return nodes.map((node) => (
      <div key={node.id}>
        <div
          className={`flex items-center py-1 px-2 hover:bg-gray-100 cursor-pointer ${
            selectedFile?.id === node.id ? 'bg-red-50 text-red-700' : 'text-gray-700'
          }`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => node.type === 'folder' ? toggleFolder(node.id) : selectFile(node)}
        >
          {node.type === 'folder' && (
            <i className={`mr-2 ${expandedFolders.has(node.id) ? 'ri-folder-open-line' : 'ri-folder-line'}`}></i>
          )}
          {node.type === 'file' && (
            <i className="ri-file-text-line mr-2"></i>
          )}
          <span className="text-sm">{node.name}</span>
          {node.type === 'file' && (
            <span className="ml-auto text-xs text-gray-500">{node.size}</span>
          )}
        </div>
        {node.type === 'folder' && expandedFolders.has(node.id) && node.children && (
          <div>
            {renderFileTree(node.children, depth + 1)}
          </div>
        )}
      </div>
    ));
  };

  const getFileContent = (file: FileNode) => {
    if (file.name.endsWith('.tsx') || file.name.endsWith('.ts')) {
      return `import React from 'react';

const ${file.name.replace('.tsx', '').replace('.ts', '')} = () => {
  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900">
        ${file.name.replace('.tsx', '').replace('.ts', '')} Component
      </h1>
      <p className="text-gray-600 mt-2">
        This is the ${file.name} component content.
      </p>
      
      {/* Component logic goes here */}
      
    </div>
  );
};

export default ${file.name.replace('.tsx', '').replace('.ts', '')};`;
    } else if (file.name === 'package.json') {
      return `{
  "name": "teamsday-admin",
  "private": true,
  "version": "0.0.0",
  "type": "module",
  "scripts": {
    "dev": "vite",
    "build": "tsc && vite build",
    "lint": "eslint . --ext ts,tsx --report-unused-disable-directives --max-warnings 0",
    "preview": "vite preview"
  },
  "dependencies": {
    "react": "^18.2.0",
    "react-dom": "^18.2.0",
    "react-router-dom": "^6.8.1"
  },
  "devDependencies": {
    "@types/react": "^18.0.27",
    "@types/react-dom": "^18.0.10",
    "@vitejs/plugin-react": "^3.1.0",
    "typescript": "^4.9.3",
    "vite": "^4.1.0"
  }
}`;
    }
    return `// ${file.name}\n// File content would be displayed here`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Code Editor</h2>
        <p className="text-gray-600">Direct code editing with syntax highlighting</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 h-screen max-h-[800px] flex">
        {/* File Explorer */}
        <div className="w-80 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
              <i className="ri-search-line absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-2">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 px-2">
              Project Files
            </div>
            {renderFileTree(fileTree)}
          </div>

          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Files: 15</span>
              <span>Size: 45.2 KB</span>
            </div>
          </div>
        </div>

        {/* Editor Area */}
        <div className="flex-1 flex flex-col">
          {/* Editor Tabs */}
          <div className="border-b border-gray-200 bg-gray-50">
            <div className="flex items-center">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('editor')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === 'editor'
                      ? 'border-red-500 text-red-600 bg-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-code-line mr-2"></i>
                  Editor
                </button>
                <button
                  onClick={() => setActiveTab('preview')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === 'preview'
                      ? 'border-red-500 text-red-600 bg-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-eye-line mr-2"></i>
                  Preview
                </button>
                <button
                  onClick={() => setActiveTab('console')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === 'console'
                      ? 'border-red-500 text-red-600 bg-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-terminal-line mr-2"></i>
                  Console
                </button>
              </div>

              <div className="ml-auto flex items-center space-x-2 px-4">
                <button
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges}
                  className={`p-1 ${hasUnsavedChanges ? 'text-red-600 hover:text-red-700' : 'text-gray-400 cursor-not-allowed'}`}
                  title="Save"
                >
                  <i className="ri-save-line"></i>
                </button>
                <button
                  onClick={handleDownload}
                  disabled={!selectedFile}
                  className={`p-1 ${selectedFile ? 'text-gray-600 hover:text-gray-800' : 'text-gray-400 cursor-not-allowed'}`}
                  title="Download"
                >
                  <i className="ri-download-line"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Editor Content */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'editor' && (
              <div className="h-full">
                {selectedFile ? (
                  <div className="h-full flex flex-col">
                    <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                      <div className="flex items-center">
                        <i className="ri-file-text-line mr-2 text-gray-500"></i>
                        <span className="text-sm font-medium text-gray-900">{selectedFile.name}</span>
                        <span className="ml-2 text-xs text-gray-500">{selectedFile.path}</span>
                        {hasUnsavedChanges && (
                          <span className="ml-2 text-xs text-red-600 font-medium">● Unsaved</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <span>{selectedFile.size}</span>
                        <span>•</span>
                        <span>Modified: {selectedFile.modified}</span>
                      </div>
                    </div>
                    
                    <div className="flex-1 bg-gray-900 overflow-auto">
                      <textarea
                        value={fileContent}
                        onChange={(e) => handleContentChange(e.target.value)}
                        className="w-full h-full p-4 text-green-400 font-mono text-sm bg-gray-900 resize-none focus:outline-none"
                        spellCheck={false}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <i className="ri-file-text-line text-4xl text-gray-400 mb-4"></i>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No File Selected</h3>
                      <p className="text-gray-500">Select a file from the explorer to start editing</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'preview' && (
              <div className="h-full bg-white p-6">
                <div className="text-center">
                  <i className="ri-eye-line text-4xl text-gray-400 mb-4"></i>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Live Preview</h3>
                  <p className="text-gray-500">Component preview would be displayed here</p>
                  {selectedFile && (
                    <p className="text-sm text-gray-400 mt-2">Previewing: {selectedFile.name}</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'console' && (
              <div className="h-full bg-black text-green-400 font-mono text-sm p-4">
                <div className="space-y-1">
                  <div className="text-blue-400">$ npm run dev</div>
                  <div>  VITE v4.1.0  ready in 1234 ms</div>
                  <div>  ➜  Local:   http://localhost:5173/</div>
                  <div>  ➜  Network: use --host to expose</div>
                  <div className="text-yellow-400">  ➜  press h to show help</div>
                  <div></div>
                  <div className="text-green-400">✓ Build completed successfully</div>
                  <div className="text-gray-400">No errors found</div>
                  <div></div>
                  <div className="flex">
                    <span className="text-blue-400">$</span>
                    <span className="ml-2 bg-gray-800 flex-1 px-2">_</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Status Bar */}
          <div className="px-4 py-2 bg-gray-100 border-t border-gray-200 flex items-center justify-between text-xs text-gray-600">
            <div className="flex items-center space-x-4">
              <span>Line 1, Column 1</span>
              <span>•</span>
              <span>UTF-8</span>
              <span>•</span>
              <span>TypeScript React</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className={hasUnsavedChanges ? 'text-yellow-600' : 'text-green-600'}>
                {hasUnsavedChanges ? '● Unsaved changes' : '✓ No errors'}
              </span>
              <span>•</span>
              <span>Auto-save: Off</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => alert('Version history feature would show file revision history')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
          >
            <i className="ri-history-line mr-2"></i>
            Version History
          </button>
          <button
            onClick={() => alert('Create branch feature would allow creating a new code branch')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
          >
            <i className="ri-git-branch-line mr-2"></i>
            Create Branch
          </button>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => alert('Build process started!\n\nNote: In production, this would compile your project.')}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 whitespace-nowrap"
          >
            <i className="ri-play-line mr-2"></i>
            Run Build
          </button>
          <button
            onClick={handleSave}
            disabled={!hasUnsavedChanges}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg whitespace-nowrap ${
              hasUnsavedChanges 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            <i className="ri-save-line mr-2"></i>
            Save All
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodeEditor;
